import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { get, put } from "../lib/api";
import { useDocumentTitle, useLoad } from "../lib/hooks";
import { Button, Card, ErrorBox, Field, Input, Loading, PageHead, Spinner, Tabs } from "../ui";
import { useShell } from "../shell/context";
import Trash from "./Trash";
import HiddenRecords from "./HiddenRecords";
import SettingsReports from "./SettingsReports";
import ClaudeAccess from "./ClaudeAccess";
import PrivacySettings from "./PrivacySettings";
import LegacyTalentSettings from "../../pages/Settings/Index";

const CADENCE_FIELDS = [
    { key: "cadence_days_A", label: "Tier A", hint: "Days between touches" },
    { key: "cadence_days_B", label: "Tier B", hint: "Days between touches" },
    { key: "cadence_days_C", label: "Tier C", hint: "Days between touches" },
    { key: "cadence_days_company", label: "Companies", hint: "Default for companies kept in touch" },
    { key: "due_soon_days", label: "Due soon window", hint: "Days ahead that count as due soon" },
];

function CrmSettings() {
    const { toast, bump } = useShell();
    const { data, error, loading, reload, setData } = useLoad(() => get("v1/settings/crm"), []);
    const [form, setForm] = useState({});
    const [busy, setBusy] = useState(false);
    const [formError, setFormError] = useState(null);

    useEffect(() => {
        if (data) setForm(Object.fromEntries(CADENCE_FIELDS.map((f) => [f.key, String(data[f.key])])));
    }, [data]);

    if (loading && !data) return <Card><Loading /></Card>;
    if (error && !data) return <ErrorBox error={error} onRetry={reload} />;

    const changes = {};
    CADENCE_FIELDS.forEach((f) => {
        if (form[f.key] !== undefined && form[f.key] !== String(data[f.key])) changes[f.key] = Number(form[f.key]);
    });
    const dirty = Object.keys(changes).length > 0;

    const save = async () => {
        for (const k of Object.keys(changes)) {
            const v = changes[k];
            const min = k === "due_soon_days" ? 0 : 1;
            const max = k === "due_soon_days" ? 365 : 3650;
            if (!Number.isInteger(v) || v < min || v > max) return setFormError(`${CADENCE_FIELDS.find((f) => f.key === k).label}: a whole number from ${min} to ${max}.`);
        }
        setBusy(true);
        setFormError(null);
        try {
            const res = await put("v1/settings/crm", changes);
            setData(res);
            toast("Settings saved. Due dates were recomputed.");
            bump();
        } catch (e) {
            setFormError(e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card
            title="Keep-in-touch cadence"
            footer={
                <div className="hz-row">
                    <span className="hz-spacer hz-small hz-muted">A custom cadence on a person or company overrides these.</span>
                    <Button variant="light" disabled={!dirty || busy} onClick={() => setForm(Object.fromEntries(CADENCE_FIELDS.map((f) => [f.key, String(data[f.key])])))}>Reset</Button>
                    <Button variant="primary" disabled={!dirty || busy} onClick={save}>{busy && <Spinner />}Save</Button>
                </div>
            }
        >
            {formError && <div className="hz-alert hz-alert-danger" style={{ marginBottom: 12 }}>{formError}</div>}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 16 }}>
                {CADENCE_FIELDS.map((f) => (
                    <Field key={f.key} label={f.label} hint={f.hint}>
                        <Input type="number" min={f.key === "due_soon_days" ? 0 : 1} value={form[f.key] || ""} onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))} />
                    </Field>
                ))}
            </div>
        </Card>
    );
}

export default function Settings() {
    const { tab = "crm" } = useParams();
    const history = useHistory();
    useDocumentTitle("Settings");
    return (
        <>
            <PageHead title="Settings" />
            <Card noBody style={{ marginBottom: 24 }}>
                <Tabs
                    active={tab}
                    onChange={(k) => history.push(`/settings/${k}`)}
                    tabs={[
                        { key: "crm", label: "CRM", icon: "ri-contacts-line" },
                        { key: "reports", label: "Reports", icon: "ri-bar-chart-box-line" },
                        { key: "claude", label: "Claude access", icon: "ri-key-2-line" },
                        { key: "privacy", label: "Privacy", icon: "ri-shield-keyhole-line" },
                        { key: "hidden", label: "Hidden", icon: "ri-eye-off-line" },
                        { key: "talent", label: "Talent searches", icon: "ri-briefcase-line" },
                        { key: "trash", label: "Trash", icon: "ri-delete-bin-line" },
                    ]}
                />
            </Card>
            {tab === "crm" && <CrmSettings />}
            {tab === "reports" && <SettingsReports />}
            {tab === "claude" && <ClaudeAccess />}
            {tab === "privacy" && <PrivacySettings />}
            {tab === "talent" && (
                <div className="hz-legacy">
                    <LegacyTalentSettings />
                </div>
            )}
            {tab === "hidden" && <HiddenRecords />}
            {tab === "trash" && <Trash />}
        </>
    );
}
